import { type FormEvent, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bird } from 'lucide-react';

import { apiFetch } from '@/lib/api';
import { setTokens, useAuthStore } from '@/stores/auth';
import { InstallButton } from '@/components/PwaInstall';
import { usePwaInstallStore } from '@/stores/pwaInstall';

interface LoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: 'bearer';
  expires_in: number;
}

interface MeResponse {
  id: number;
  username: string;
  nombre: string | null;
  rol: 'admin' | 'operario' | 'ganadero' | 'veterinario';
}

interface LocationState {
  from?: { pathname: string };
}

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const setUser = useAuthStore((s) => s.setUser);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const tokens = await apiFetch<LoginResponse>('/v1/auth/login', {
        method: 'POST',
        body: JSON.stringify({ username, password }),
        auth: false,
      });
      setTokens(tokens.access_token, tokens.refresh_token);

      const me = await apiFetch<MeResponse>('/v1/auth/me');
      usePwaInstallStore.getState().noteSuccessfulLogin();
      setUser(me);

      const from = (location.state as LocationState | null)?.from?.pathname ?? '/';
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('login.error'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="container mx-auto flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-sm rounded-lg border border-border bg-surface p-6 shadow-sm">
        <h1 className="ui-brand mb-2 justify-center">
          <Bird aria-hidden strokeWidth={1.8} />
          <span>Camadero</span>
        </h1>
        <p className="mt-1 text-center text-sm text-text-secondary">{t('app.tagline')}</p>

        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-text-primary">
              {t('login.username')}
            </label>
            <input
              id="username"
              type="text"
              inputMode="text"
              autoComplete="username"
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="ui-field mt-1"
              disabled={submitting}
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-text-primary">
              {t('login.password')}
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="ui-field mt-1"
              disabled={submitting}
            />
          </div>

          {error && (
            <p
              role="alert"
              className="rounded-md border border-warning bg-warning-bg px-3 py-2 text-sm text-warning-text"
            >
              {error}
            </p>
          )}

          <button type="submit" disabled={submitting} className="ui-primary w-full">
            {submitting ? t('login.submitting') : t('login.submit')}
          </button>
          <InstallButton />

          <p className="text-center text-xs text-text-muted">
            <Link to="/" className="hover:underline">
              ← {t('login.back_home')}
            </Link>
          </p>
        </form>
      </div>
    </main>
  );
}

export default LoginPage;
